import { useState, useCallback, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useSupabaseCandidateCache } from './useSupabaseCandidateCache';
import type { CandidateData } from '../services/candidateCache';
import { useAuth } from '../context/AuthContext';
import { useToast } from './useToast';

// Enhanced candidate interface for interview management
export interface InterviewCandidate extends CandidateData {
  analysisScore?: number;
  lastAnalyzed?: Date;
  verdict?: string;
  technicalSkills?: Array<{
    skill: string;
    proficiency: string;
    evidence: string;
  }>;
  interviewQuestions?: {
    technical: string[];
    behavioral: string[];
    cultural: string[];
    riskFactors: string[];
    redFlags: string[];
    greenFlags: string[];
  };
  analysisId?: string;
  interviewPerspectives?: Record<string, string>;
}

interface UseInterviewCandidateCacheOptions {
  projectId?: string;
  enableAutoRefresh?: boolean;
  refreshIntervalMinutes?: number;
  cacheTimeMinutes?: number;
  maxCandidates?: number;
}

interface UseInterviewCandidateCacheReturn {
  candidates: InterviewCandidate[];
  isLoading: boolean;
  isFromCache: boolean;
  error: string | null;
  refreshCandidates: () => Promise<void>;
  invalidateCache: () => Promise<void>;
  addCandidate: (candidate: InterviewCandidate) => Promise<void>;
  removeCandidate: (candidateId: string) => Promise<void>;
  getCacheInfo: () => Promise<{
    hitRate: number;
    totalEntries: number;
    lastRefresh: Date | null;
    cacheSize: string;
  }>;
  warmCache: () => Promise<void>;
}

// Helper functions for data processing
function extractNames(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: 'Unknown', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  
  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');
  return { firstName, lastName };
}

function parseInterviewPerspectives(analysis: any): Record<string, string> {
  if (!analysis.interview_perspectives) return {};
  
  try {
    if (typeof analysis.interview_perspectives === 'string') {
      return JSON.parse(analysis.interview_perspectives);
    }
    return analysis.interview_perspectives || {};
  } catch (error) {
    console.error('Error parsing interview perspectives:', error);
    return {};
  }
}

function generateInterviewQuestions(analysis: any): {
  technical: string[];
  behavioral: string[];
  cultural: string[];
  riskFactors: string[];
  redFlags: string[];
  greenFlags: string[];
} {
  // This is a simplified version - you can enhance this based on your existing logic
  const emptyQuestions = {
    technical: [],
    behavioral: [],
    cultural: [],
    riskFactors: [],
    redFlags: [],
    greenFlags: []
  };

  try {
    const perspectives = parseInterviewPerspectives(analysis);
    const defaultPerspective = perspectives.manager || Object.values(perspectives)[0];
    
    if (!defaultPerspective) {
      return emptyQuestions;
    }

    // Extract questions from perspective content
    // This would use your existing parsing logic
    return emptyQuestions; // Placeholder - implement your existing logic here
  } catch (error) {
    console.error('Error generating interview questions:', error);
    return emptyQuestions;
  }
}

// Optimized data fetching function
async function fetchInterviewCandidates(
  projectId?: string,
  maxCandidates: number = 100
): Promise<InterviewCandidate[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  console.log('🚀 Fetching interview candidates for project:', projectId);
  
  // Build optimized query
  let query = supabase
    .from('analysis')
    .select(`
      id,
      candidate_id,
      job_id,
      fit_score,
      verdict,
      reasoning,
      technical_skills,
      risk_factors,
      interview_plan,
      interview_perspectives,
      created_at,
      candidates!inner (
        id,
        name,
        created_at
      ),
      jobs!inner (
        id,
        title,
        company
      )
    `)
    .eq('user_id', user.id)
    .not('candidates', 'is', null)
    .not('jobs', 'is', null)
    .order('created_at', { ascending: false })
    .limit(maxCandidates);

  // Add project filtering if specified
  if (projectId) {
    const { data: projectCandidates } = await supabase
      .from('project_candidates')
      .select('analysis_id')
      .eq('project_id', projectId);
    
    if (projectCandidates?.length) {
      const analysisIds = projectCandidates.map(pc => pc.analysis_id).filter(Boolean);
      if (analysisIds.length) {
        query = query.in('id', analysisIds);
      }
    }
  }

  const { data: analysisData, error } = await query;
  
  if (error) {
    throw new Error(`Failed to fetch analysis data: ${error.message}`);
  }

  if (!analysisData?.length) {
    return [];
  }

  console.log(`📊 Processing ${analysisData.length} analysis records...`);
  
  // Process candidates efficiently
  const candidates: InterviewCandidate[] = [];
  
      for (const analysis of analysisData) {
    try {
      // Handle array or object structure for candidates/jobs
      const candidateData = Array.isArray(analysis.candidates) ? analysis.candidates[0] : analysis.candidates;
      const jobData = Array.isArray(analysis.jobs) ? analysis.jobs[0] : analysis.jobs;
      
      if (!candidateData || !jobData) continue;

      const { firstName, lastName } = extractNames(candidateData.name);
      const technicalSkills = Array.isArray(analysis.technical_skills) 
        ? analysis.technical_skills 
        : [];
      const skillsArray = technicalSkills.map((ts: any) => ts.skill || ts.name || String(ts)).filter(Boolean);
      
      // Generate interview data
      const interviewQuestions = generateInterviewQuestions(analysis);
      const interviewPerspectives = parseInterviewPerspectives(analysis);

      const candidate: InterviewCandidate = {
        id: analysis.id,
        name: candidateData.name,
        firstName,
        lastName,
        email: `${firstName.toLowerCase()}${lastName ? '.' + lastName.toLowerCase() : ''}@email.com`,
        position: jobData.title || 'Not specified',
        jobDescription: '', // Skip for performance
        analysisScore: analysis.fit_score,
        lastAnalyzed: new Date(analysis.created_at),
        status: 'new' as const,
        skills: skillsArray,
        relevanceScore: analysis.fit_score || 0,
        verdict: analysis.verdict,
        technicalSkills,
        interviewQuestions,
        projectId,
        analysisId: analysis.id,
        interviewPerspectives,
      };
      
      candidates.push(candidate);
    } catch (error) {
      console.error(`Error processing analysis ${analysis.id}:`, error);
      continue;
    }
  }

  console.log(`✅ Successfully processed ${candidates.length} interview candidates`);
  return candidates;
}

export function useInterviewCandidateCache(
  options: UseInterviewCandidateCacheOptions = {}
): UseInterviewCandidateCacheReturn {
  const {
    projectId,
    enableAutoRefresh = true,
    refreshIntervalMinutes = 5,
    cacheTimeMinutes = 30,
    maxCandidates = 100
  } = options;

  const { user } = useAuth();
  const { addToast } = useToast();
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    lastFetchTime: 0,
    averageFetchTime: 0,
    fetchCount: 0
  });

  // Memoized fetch function
  const fetchFunction = useCallback(async (): Promise<InterviewCandidate[]> => {
    const startTime = Date.now();
    
    try {
      const result = await fetchInterviewCandidates(projectId, maxCandidates);
      const fetchTime = Date.now() - startTime;
      
      // Update performance metrics
      setPerformanceMetrics(prev => ({
        lastFetchTime: fetchTime,
        averageFetchTime: prev.fetchCount > 0 
          ? (prev.averageFetchTime * prev.fetchCount + fetchTime) / (prev.fetchCount + 1)
          : fetchTime,
        fetchCount: prev.fetchCount + 1
      }));
      
      setLastRefresh(new Date());
      console.log(`⚡ Interview candidates fetched in ${fetchTime}ms`);
      
      return result;
    } catch (error) {
      console.error('❌ Error fetching interview candidates:', error);
      addToast(`Failed to load candidates: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
      throw error;
    }
  }, [projectId, maxCandidates, addToast]);

  // Enhanced cache configuration
  const cacheKey = useMemo(
    () => `interview_candidates_v2_${projectId || 'all'}`,
    [projectId]
  );

  const {
    candidates,
    isLoading,
    isFromCache,
    refreshCache,
    clearCache,
    setCandidates,
    error,
    getCacheMetrics
  } = useSupabaseCandidateCache(fetchFunction, {
    cacheKey,
    ttlMinutes: cacheTimeMinutes,
    enableBackgroundRefresh: enableAutoRefresh,
    autoMigrate: true
  });

  // Auto-refresh mechanism
  useEffect(() => {
    if (!enableAutoRefresh || !user) return;

    const interval = setInterval(async () => {
      try {
        console.log('🔄 Auto-refreshing interview candidates...');
        await refreshCache();
      } catch (error) {
        console.error('❌ Auto-refresh failed:', error);
      }
    }, refreshIntervalMinutes * 60 * 1000);

    return () => clearInterval(interval);
  }, [enableAutoRefresh, refreshIntervalMinutes, refreshCache, user]);

  // Enhanced refresh function with feedback
  const refreshCandidates = useCallback(async (): Promise<void> => {
    try {
      addToast('Refreshing candidates...', 'info');
      await refreshCache();
      addToast('Candidates refreshed successfully', 'success');
    } catch (error) {
      addToast('Failed to refresh candidates', 'error');
      throw error;
    }
  }, [refreshCache, addToast]);

  // Cache invalidation
  const invalidateCache = useCallback(async (): Promise<void> => {
    try {
      await clearCache();
      addToast('Cache cleared successfully', 'success');
    } catch (error) {
      addToast('Failed to clear cache', 'error');
      throw error;
    }
  }, [clearCache, addToast]);

  // Add candidate to cache
  const addCandidate = useCallback(async (candidate: InterviewCandidate): Promise<void> => {
    const updatedCandidates = [candidate, ...candidates];
    await setCandidates(updatedCandidates);
  }, [candidates, setCandidates]);

  // Remove candidate from cache
  const removeCandidate = useCallback(async (candidateId: string): Promise<void> => {
    const updatedCandidates = candidates.filter(c => c.id !== candidateId);
    await setCandidates(updatedCandidates);
  }, [candidates, setCandidates]);

  // Get cache information
  const getCacheInfo = useCallback(async () => {
    const metrics = await getCacheMetrics();
    const hitRate = metrics 
      ? (metrics.hitCount / (metrics.hitCount + metrics.missCount)) * 100 
      : 0;
    
    return {
      hitRate,
      totalEntries: metrics?.totalSize || 0,
      lastRefresh,
      cacheSize: `${performanceMetrics.lastFetchTime}ms avg`
    };
  }, [getCacheMetrics, lastRefresh, performanceMetrics.lastFetchTime]);

  // Warm cache by pre-fetching data
  const warmCache = useCallback(async (): Promise<void> => {
    try {
      console.log('🔥 Warming interview candidate cache...');
      await refreshCache();
    } catch (error) {
      console.error('❌ Cache warming failed:', error);
    }
  }, [refreshCache]);

  // Log performance metrics periodically
  useEffect(() => {
    if (performanceMetrics.fetchCount > 0) {
      console.log('📊 Interview cache performance:', {
        averageFetchTime: `${performanceMetrics.averageFetchTime.toFixed(0)}ms`,
        lastFetchTime: `${performanceMetrics.lastFetchTime}ms`,
        totalFetches: performanceMetrics.fetchCount,
        candidatesCount: candidates.length,
        hitRate: isFromCache ? 'Cache Hit' : 'Cache Miss'
      });
    }
  }, [performanceMetrics, candidates.length, isFromCache]);

  return {
    candidates: candidates as InterviewCandidate[],
    isLoading,
    isFromCache,
    error,
    refreshCandidates,
    invalidateCache,
    addCandidate,
    removeCandidate,
    getCacheInfo,
    warmCache
  };
} 